// "After reserving as a guest": the thank-you page's invitation to put the
// booking on an account. Rendered by @majourneys/booking through the
// provider's `AccountPrompt` slot, so the city sites (no slot) never see it.
//
// What signing in really does, so the copy can say only that: the account's
// booking list shows reservations owned by the account, and a guest booking is
// owned by nobody. Starting a sign-in here leaves the booking's (reference,
// email) pair behind; GuestBookingClaimer sends it to the existing claim
// route once the session exists, and the booking then shows in the account.
// Points are NOT promised: the API awards them when a signed-in guest books,
// and claiming a booking afterwards does not award any.
//
// Drawn as the booking ticket's tear-off stub (owner, 2026-10-23): a tear
// line, then one "Email me a code" button for the booking's address. Once the
// guest is signed in the stub tears off and flies to the account icon, and a
// "kept" line stays in its place.
//
// Hidden when the guest is signed in, when the page does not know the
// booking's email (nothing to claim with), and after "Not now" for that
// booking, which is remembered in this browser. Right after "Not now" a slim
// line stays, so a guest who tapped it by mistake can take it back.
import { useEffect, useRef, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PW, TicketPerforation, type AccountPromptProps } from "@majourneys/booking";
import { Icon } from "@majourneys/icons";
import { useSession } from "../../../lib/auth";
import { SignInForm } from "./sign-in-form";
import { addPendingClaim, removePendingClaim } from "../lib/post-sign-in";

export const KEEP_BOOKING_DISMISSED_KEY = "mj-keep-booking-dismissed";

function readDismissed(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEEP_BOOKING_DISMISSED_KEY);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function forgetDismissed(reference: string) {
  try {
    window.localStorage.setItem(
      KEEP_BOOKING_DISMISSED_KEY,
      JSON.stringify(readDismissed().filter((r) => r !== reference)),
    );
  } catch {
    // Private mode: nothing was stored.
  }
}

function rememberDismissed(reference: string) {
  try {
    const next = [reference, ...readDismissed().filter((r) => r !== reference)].slice(0, 50);
    window.localStorage.setItem(KEEP_BOOKING_DISMISSED_KEY, JSON.stringify(next));
  } catch {
    // Private mode: it comes back on the next visit, which is harmless.
  }
}

/** How long the torn stub takes to reach the account icon (mj-stub-tear). */
const TEAR_MS = 720;

/**
 * Flies a copy of the stub to the account icon. A copy, fixed above the page,
 * because the pass and the booking sheet both clip their contents: the real
 * stub would be cut off at their edge. Reduced motion: the CSS skips it.
 */
function flyToAccount(node: HTMLElement | null) {
  if (!node || typeof document === "undefined") return;
  const from = node.getBoundingClientRect();
  const anchor = [...document.querySelectorAll<HTMLElement>("[data-account-anchor]")]
    .map((el) => el.getBoundingClientRect())
    .find((r) => r.width > 0 && r.height > 0 && r.bottom > 0 && r.top < window.innerHeight);
  const ghost = node.cloneNode(true) as HTMLElement;
  ghost.setAttribute("aria-hidden", "true");
  ghost.removeAttribute("data-testid");
  Object.assign(ghost.style, {
    position: "fixed",
    left: `${from.left}px`,
    top: `${from.top}px`,
    width: `${from.width}px`,
    margin: "0",
    zIndex: "9999",
    pointerEvents: "none",
    background: "#FBFBFD",
    borderRadius: "18px",
  });
  ghost.style.setProperty("--fly-x", `${anchor ? anchor.left + anchor.width / 2 - (from.left + from.width / 2) : 40}px`);
  ghost.style.setProperty("--fly-y", `${anchor ? anchor.top + anchor.height / 2 - (from.top + from.height / 2) : -240}px`);
  ghost.classList.add("mj-stub-tear");
  document.body.appendChild(ghost);
  window.setTimeout(() => ghost.remove(), TEAR_MS + 60);
}

export function KeepBookingCard({ reference, email }: AccountPromptProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const { data: session, isPending } = useSession();
  // Read after mount: the server has no localStorage, and the first client
  // render has to match the server's.
  const [dismissed, setDismissed] = useState<boolean | null>(null);
  // Once the guest is signing in here, the card stays until the form is done,
  // so the first-name step is not cut off when the session lands.
  const [inFlow, setInFlow] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDismissed(readDismissed().includes(reference));
  }, [reference]);

  const stubRef = useRef<HTMLDivElement>(null);
  /** "Not now" this visit: a slim line instead of nothing, to undo it. */
  const [skipped, setSkipped] = useState(false);

  const signedIn = !!session?.user;
  if (done) {
    return (
      <div data-testid="keep-booking-kept">
        <TicketPerforation />
        <div className="mj-stub-rise flex items-center gap-3 px-5 pb-5 pt-1" role="status">
          <span
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
            style={{ background: PW.greenTint }}
            aria-hidden="true"
          >
            <Icon name="ri-check-double-line" className="text-[18px]" style={{ color: PW.green }} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="m-0 font-sans text-[14px] font-bold" style={{ color: PW.navy }}>
              {t("ticket.kept")}
            </p>
            <p className="m-0 text-balance font-sans text-[12.5px] font-medium leading-snug" style={{ color: PW.muted }}>
              {t("ticket.kept_sub")}
            </p>
          </div>
        </div>
      </div>
    );
  }
  if (skipped && email) {
    return (
      <div data-testid="keep-booking-skipped">
        <TicketPerforation />
        <div className="flex items-center gap-3 px-5 pb-4 pt-1">
          <Icon name="ri-smartphone-line" className="shrink-0 text-[17px]" style={{ color: PW.navy }} aria-hidden="true" />
          <p className="m-0 min-w-0 flex-1 text-balance font-sans text-[12.5px] font-medium" style={{ color: PW.muted }}>
            {t("ticket.skipped")}
          </p>
          <button
            type="button"
            onClick={() => {
              forgetDismissed(reference);
              setSkipped(false);
              setDismissed(false);
            }}
            className="min-h-[32px] shrink-0 rounded-full bg-white px-3 font-sans text-[12.5px] font-bold"
            style={{ color: PW.navy, boxShadow: `0 0 0 1px ${PW.controlEdge}` }}
          >
            {t("ticket.keep_stub")}
          </button>
        </div>
      </div>
    );
  }
  if (!email || dismissed !== false) return null;
  if (!inFlow && (isPending || signedIn)) return null;

  return (
    <div data-testid="keep-booking-card">
      <TicketPerforation />
      <div ref={stubRef} className="px-5 pb-5 pt-1">
        <SignInForm
          variant="stub"
          initialEmail={email}
          autoFocus={false}
          redirectPath={location.href}
          onStart={() => {
            setInFlow(true);
            addPendingClaim({ reference, email });
          }}
          onSignedIn={() => {
            flyToAccount(stubRef.current);
            setDone(true);
          }}
          footerEnd={
            !inFlow ? (
              <button
                type="button"
                onClick={() => {
                  rememberDismissed(reference);
                  removePendingClaim(reference);
                  setDismissed(true);
                  setSkipped(true);
                }}
                className="min-h-8 px-3 font-sans text-[13px] font-semibold"
                style={{ color: PW.muted }}
              >
                {t("ticket.not_now")}
              </button>
            ) : null
          }
        />
      </div>
    </div>
  );
}
